import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import staffService from '@/services/staffService';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface StaffUser {
  id: string;
  username: string;
}

interface ResetPasswordDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: StaffUser | null;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({ 
  isOpen, 
  onOpenChange, 
  selectedUser 
}) => {
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState('');
  
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string, password: string }) => 
      staffService.resetStaffPassword(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffUsers'] });
      toast('Password Reset', {
        description: 'The password has been reset successfully.'
      });
      onOpenChange(false);
      setNewPassword('');
    },
    onError: (error: any) => {
      toast('Error', {
        description: error.message || 'Failed to reset password.'
      });
    }
  });

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser && newPassword) {
      resetPasswordMutation.mutate({ 
        id: selectedUser.id,
        password: newPassword
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            Set a new password for {selectedUser?.username}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleResetPassword}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="new-password" className="text-right">
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={resetPasswordMutation.isPending}>
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordDialog;
